/**
 * BranchSelector - Dropdown to select a branch with pagination support.
 */

import { useState, useMemo } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, GitBranch, Shield, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface Branch {
  name: string;
  sha: string;
  protected: boolean;
}

interface BranchSelectorProps {
  branches: Branch[];
  selectedBranch: string | null;
  onSelect: (branch: string | null) => void;
  hasMore?: boolean;
  isLoading?: boolean;
  onLoadMore?: () => void;
}

export function BranchSelector({
  branches,
  selectedBranch,
  onSelect,
  hasMore,
  isLoading,
  onLoadMore,
}: BranchSelectorProps) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => branches.find((b) => b.name === selectedBranch),
    [branches, selectedBranch]
  );

  const handleLoadMore = () => {
    onLoadMore?.();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[280px] justify-between"
        >
          <div className="flex items-center gap-2 truncate">
            <GitBranch className="w-4 h-4 flex-shrink-0" />
            {selected ? (
              <>
                <span className="truncate">{selected.name}</span>
                {selected.protected && (
                  <Shield className="w-3 h-3 text-amber-500 flex-shrink-0" />
                )}
              </>
            ) : (
              <span className="text-muted-foreground">Select branch...</span>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0">
        <Command>
          <CommandInput placeholder="Search branches..." />
          <CommandList>
            <CommandEmpty>No branch found.</CommandEmpty>
            <CommandGroup>
              {branches.map((branch) => (
                <CommandItem
                  key={branch.name}
                  value={branch.name}
                  onSelect={() => {
                    onSelect(branch.name);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selectedBranch === branch.name ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <GitBranch className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span className="truncate flex-1">{branch.name}</span>
                  {branch.protected && (
                    <Shield className="ml-2 w-3 h-3 text-amber-500" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
            {hasMore && onLoadMore && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={handleLoadMore}
                    className="justify-center text-muted-foreground"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      "Load more branches..."
                    )}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
